import Avatar from "components/common/Avatar";
import CopyableText from "components/common/CopyableText";
import SubtleBadge from "components/common/SubtleBadge";
import useCredits from "hooks/donations/useCredits";
import useFormattedDate from "hooks/useFormatDate";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { PurposesTable } from "../sections/PurposesTable";

export const CreditDetails = () => {
  const { creditId } = useParams();
  const navigate = useNavigate();
  const [credit, setCredit] = useState(null);

  const { loading, fetchCreditById, error } = useCredits();
  const { formatDateTime } = useFormattedDate();

  useEffect(() => {
    const fetchCredit = async () => {
      if (!creditId) return;
      try {
        const credit = await fetchCreditById(creditId);
        setCredit(credit);
      } catch (err) {
        setCredit(null);
      }
    };
    fetchCredit();
  }, [creditId]);

  // destruction the credit value details
  const {
    id,
    code,
    unit,
    currency,
    status,
    issuedAt,
    expiresAt,
    amount,
    balance,
    donation,
    donee,
  } = credit || {};

  const donationStatusMap = {
    ACTIVE: { type: "success", label: "Active" },
    PENDING: { type: "warning", label: "Pending" },
    EXPIRED: { type: "danger", label: "Expired" },
    DELETED: { type: "danger", label: "Deleted" },
  };
  const { type: donationStatusType, label: donationStatusLabel } =
    donationStatusMap[donation?.status] || {
      type: "secondary",
      label: "Unknown",
    };

  const creditStatusMap = {
    ACTIVE: { type: "success", label: "Active" },
    REDEEMED: { type: "info", label: "Redeemed" },
    EXPIRED: { type: "danger", label: "Expired" },
    DELETED: { type: "danger", label: "Deleted" },
  };

  const { type: creditStatusType, label: creditStatusLabel } = creditStatusMap[
    status
  ] || { type: "secondary", label: "Unknown" };

  if (loading && !credit) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <Card className="mb-4 shadow-none mt-3 rounded border-1">
        <Card.Body className="d-flex align-items-center py-3 px-4">
          {/* Left side: Back button */}
          <div className="d-flex align-items-center" style={{ minWidth: 90 }}>
            <Button
              type="button"
              onClick={() => navigate("/olitask/donations/credits")}
              size="sm"
              aria-label="Go back"
              variant="outline-success"
            >
              <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
            </Button>
          </div>

          {/* Center: Title */}
          <div className="px-3">
            <h4 className="mb-0 fw-semibold text-base">Credit details</h4>
          </div>

          {/* Right side: Action buttons */}
        </Card.Body>
      </Card>

      <Row className="g-3">
        {/* credit info */}
        <Col lg={4} className="mb-3">
          <Card className="pb-2">
            <Card.Header>
              <h6 className="mb-0 fw-bold ps-2dd">Credit Information</h6>
            </Card.Header>
            <Card.Body className="py-0">
              <div className="mb-2">
                <label className="form-label text-xxs mb-0">Code</label>
                <h6 className="mb-0 fw-semibold text-sm">{code || "NULL"}</h6>
              </div>
              <div className="d-flex justify-content-between">
                <div className="flex-1 mb-2">
                  <label className="form-label text-xxs mb-0">Unit</label>
                  <h6 className="mb-0 fw-semibold text-sm">{unit}</h6>
                </div>
                <div className="flex-1 mb-2">
                  <label className="form-label text-xxs mb-0">Currency</label>
                  <h6 className="mb-0 fw-semibold text-sm">{currency}</h6>
                </div>
              </div>

              <div className="d-flex justify-content-between">
                <div className="flex-1 mb-2">
                  <label className="form-label text-xxs mb-0">Amount</label>
                  <h6 className="mb-0 fw-semibold text-sm">
                    ${amount?.toFixed(2)}
                  </h6>
                </div>
                <div className="flex-1 mb-2">
                  <label className="form-label text-xxs mb-0">Balance</label>
                  <h6 className="mb-0 fw-semibold text-sm">
                    ${balance?.toFixed(2)}
                  </h6>
                </div>
              </div>
              <div className="flex-1 mb-2">
                <label className="form-label text-xxs mb-0">Issued At</label>
                <h6 className="mb-0 fw-semibold text-sm">
                  {formatDateTime(issuedAt)}
                </h6>
              </div>
              <div className="flex-1 mb-2">
                <label className="form-label text-xxs mb-0">Expires At</label>
                <h6 className="mb-0 fw-semibold text-sm">
                  {formatDateTime(expiresAt)}
                </h6>
              </div>
              <div className="mb-2">
                <label className="form-label text-xxs mb-0 d-block">
                  Status
                </label>
                <SubtleBadge pill bg={creditStatusType}>
                  {creditStatusLabel}
                </SubtleBadge>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* donor info */}
        <Col lg={4} className="mb-3">
          <Card>
            <Card.Header>
              <h6 className="mb-0 fw-bold text-center ps-2dd">
                Donor Information
              </h6>
            </Card.Header>
            <Card.Body className="py-0">
              {/* Profile Photo */}
              <div className="d-flex align-items-center justify-content-center ps-2dd">
                {donation?.donor.profilePhoto ? (
                  <Avatar
                    src={donation?.donor.profilePhoto}
                    size="4xl"
                    className="mb-3"
                  />
                ) : (
                  <Avatar
                    size="4xl"
                    name={
                      `${donation?.donor.firstName || ""} ${
                        donation?.donor.lastName || ""
                      }`.trim() || donation?.donor.nickName
                    }
                    className="mb-3"
                  />
                )}
              </div>

              <div className="mb-3 ps-2">
                <div className="mb-1">
                  <label className="form-label text-xxs mb-0">Full name</label>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {`${donation?.donor.firstName || ""} ${
                      donation?.donor.lastName || ""
                    }`.trim() || donation?.donor.nickName}
                  </h6>
                </div>
                <div className="mb-1">
                  <label className="form-label text-xxs mb-0">Email</label>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {donation?.donor.email}
                  </h6>
                </div>
                <div className="mb-2">
                  <label className="form-label text-xxs mb-0">Phone</label>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {donation?.donor.phone}
                  </h6>
                </div>
                <div className="mb-2">
                  <label className="form-label text-xxs mb-0">Gender</label>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {donation?.donor.gender || "Not provided"}
                  </h6>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* donee info */}
        {donee && (
          <Col lg={4} className="mb-3">
            <Card>
              <Card.Header>
                <h6 className="mb-0 fw-bold text-center">Donee Information</h6>
              </Card.Header>
              <Card.Body className="py-0">
                {/* Profile Photo */}
                <div className="d-flex align-items-center justify-content-center">
                  {donee?.profilePhoto ? (
                    <Avatar
                      src={donee?.profilePhoto}
                      size="4xl"
                      className="mb-3"
                    />
                  ) : (
                    <Avatar
                      size="4xl"
                      name={
                        `${donee?.firstName || ""} ${
                          donee?.lastName || ""
                        }`.trim() || donee?.nickName
                      }
                      className="mb-3"
                    />
                  )}
                </div>

                <div className="mb-3 ps-2">
                  <div className="mb-1">
                    <label className="form-label text-xxs mb-0">
                      Full name
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {`${donee?.firstName || ""} ${
                        donee?.lastName || ""
                      }`.trim() || donee?.nickName}
                    </h6>
                  </div>
                  <div className="mb-1">
                    <label className="form-label text-xxs mb-0">Email</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {donee?.email || "Not provided"}
                    </h6>
                  </div>
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">Phone</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {donee?.phone || "Not provided"}
                    </h6>
                  </div>
                  <div className="">
                    <label className="form-label text-xxs mb-0">Gender</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {donee?.gender || "Not provided"}
                    </h6>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* additional information */}
        <Col lg={12} className="mb-3">
          <Card>
            <Card.Header>
              <h6 className="mb-0 fw-bold ps-2">Additional Information</h6>
            </Card.Header>
            <Card.Body className="py-0 pb-2">
              <Row>
                <Col>
                  <div className="mb-3 ps-2">
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Donation ID
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        <CopyableText value={donation?.id} />
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">Code</label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {donation?.code || "NULL"}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">Amount</label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        ${donation?.amount ? donation?.amount.toFixed(2) : 0.0}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Balance
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        $
                        {donation?.balance ? donation?.balance.toFixed(2) : 0.0}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Currency
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {donation?.currency}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Transaction ID
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        <CopyableText
                          value={donation?.transactionId}
                          displayValue={donation?.transactionId || "Not provided"}
                        />
                      </h6>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="mb-3 ps-2">
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0 d-block">
                        Status
                      </label>
                      <SubtleBadge pill bg={donationStatusType}>
                        {donationStatusLabel}
                      </SubtleBadge>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Created At
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {formatDateTime(donation?.createdAt)}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Updated At
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {formatDateTime(donation?.updatedAt) || "N/A"}
                      </h6>
                    </div>

                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Short Note
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {donation?.note || "Not provided"}
                      </h6>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* purposes Information */}
        <Col lg={12}>
          <PurposesTable
            purposes={donation?.purposes}
            title="Credit purposes"
          />
        </Col>
      </Row>
    </>
  );
};
